import React from "react";
import { Button, Link } from "@heroui/react";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { useTrialSignupHref } from "../lib/signupUrl";

const viewport = { once: true, amount: 0.35 } as const;

/** Right-side ambient + SVG decor (isolated ids for SSR-safe single instance). */
function FooterCTARightBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute top-0 right-0 bottom-0 w-[min(100%,520px)] translate-x-[18%] sm:w-[min(100%,580px)] sm:translate-x-[10%] md:translate-x-[4%] lg:w-[min(100%,640px)] lg:translate-x-0"
    >
      {/* Soft volumetric stack — cyan → white mist */}
      <div
        className="absolute top-[8%] right-[-12%] h-[88%] w-[95%] rounded-[46%] opacity-90"
        style={{
          background:
            "radial-gradient(ellipse 72% 58% at 58% 42%, rgba(255,255,255,0.16) 0%, rgba(0,183,255,0.12) 22%, rgba(0,183,255,0.05) 48%, transparent 70%)",
          filter: "blur(1px)",
        }}
      />
      <div className="absolute top-[28%] right-[6%] h-[42%] w-[42%] rounded-full bg-[#00b7ff] opacity-[0.07] blur-[64px]" />
      <div className="absolute top-[52%] right-[18%] h-[28%] w-[36%] rounded-full bg-white opacity-[0.06] blur-[48px]" />

      {/* Diagonal glass shard */}
      <div
        className="absolute top-[12%] right-[4%] h-[78%] w-[38%] rotate-[14deg] rounded-full opacity-[0.12]"
        style={{
          background:
            "linear-gradient(165deg, rgba(255,255,255,0.2) 0%, rgba(0,183,255,0.15) 35%, transparent 72%)",
        }}
      />

      <svg
        className="absolute inset-0 h-full w-full overflow-visible"
        fill="none"
        preserveAspectRatio="xMaxYMid slice"
        viewBox="0 0 520 580"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient
            id="footerCtaStroke"
            gradientUnits="userSpaceOnUse"
            x1="280"
            x2="520"
            y1="120"
            y2="420"
          >
            <stop offset="0%" stopColor="#00b7ff" stopOpacity="0" />
            <stop offset="28%" stopColor="#00b7ff" stopOpacity="0.65" />
            <stop offset="55%" stopColor="#ffffff" stopOpacity="0.45" />
            <stop offset="82%" stopColor="#00b7ff" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#00b7ff" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="footerCtaArc" x1="0%" x2="100%" y1="0%" y2="100%">
            <stop offset="0%" stopColor="#00b7ff" stopOpacity="0.15" />
            <stop offset="50%" stopColor="#ffffff" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#00b7ff" stopOpacity="0.2" />
          </linearGradient>
          <radialGradient id="footerCtaCore" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.14" />
            <stop offset="42%" stopColor="#00b7ff" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#00b7ff" stopOpacity="0" />
          </radialGradient>
          <filter id="footerCtaSoft" height="140%" width="140%" x="-20%" y="-20%">
            <feGaussianBlur in="SourceGraphic" result="b" stdDeviation="0.8" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <circle cx="402" cy="278" fill="url(#footerCtaCore)" r="108" />

        <circle
          cx="418"
          cy="288"
          opacity="0.85"
          r="232"
          stroke="url(#footerCtaStroke)"
          strokeWidth="1.15"
          transform="rotate(-8 418 288)"
        />
        <circle
          cx="418"
          cy="288"
          opacity="0.5"
          r="196"
          stroke="url(#footerCtaStroke)"
          strokeWidth="0.65"
          transform="rotate(-8 418 288)"
        />
        <circle
          cx="418"
          cy="288"
          opacity="0.38"
          r="162"
          stroke="url(#footerCtaStroke)"
          strokeDasharray="3 11"
          strokeWidth="0.85"
          transform="rotate(-8 418 288)"
        />

        <path
          d="M 52 455 C 180 380, 260 200, 445 118"
          filter="url(#footerCtaSoft)"
          opacity="0.55"
          stroke="url(#footerCtaArc)"
          strokeLinecap="round"
          strokeWidth="1.25"
        />
        <path
          d="M 118 498 C 220 410, 300 260, 488 168"
          opacity="0.35"
          stroke="url(#footerCtaArc)"
          strokeDasharray="6 18"
          strokeLinecap="round"
          strokeWidth="0.9"
        />

        <g opacity="0.7">
          <circle cx="318" cy="168" fill="#00b7ff" r="2.5" />
          <circle cx="348" cy="198" fill="#ffffff" r="1.8" opacity="0.85" />
          <circle cx="278" cy="338" fill="#00b7ff" r="2" opacity="0.55" />
          <circle cx="388" cy="412" fill="#ffffff" r="1.5" opacity="0.6" />
        </g>
      </svg>
    </div>
  );
}

export default function FooterCTASection() {
  const signupHref = useTrialSignupHref("footer-cta");

  return (
    <section
      aria-labelledby="footer-cta-heading"
      className="relative isolate mt-16 w-full overflow-hidden border-t border-default-100/50 bg-background"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_88%_48%_at_50%_-26%,rgba(0,183,255,0.11),transparent_58%)]"
      />

      <FooterCTARightBackdrop />

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.032] [background-image:linear-gradient(rgba(255,255,255,0.07)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.07)_1px,transparent_1px)] [background-size:44px_44px]"
      />

      <div className="relative mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-14 sm:flex-row sm:items-end sm:justify-between sm:gap-12 sm:px-8 sm:py-16 lg:py-20">
        <LazyMotion features={domAnimation}>
          <div className="flex max-w-2xl flex-col gap-5">
            <m.h2
              className="text-foreground text-[1.35rem] leading-snug font-semibold tracking-tight sm:text-3xl sm:leading-tight lg:text-[2rem] lg:leading-[1.25]"
              id="footer-cta-heading"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.55, delay: 0.06, ease: [0.22, 1, 0.36, 1] }}
              viewport={viewport}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <span className="text-default-500 block font-normal">
                把客户对话，
              </span>
              <span className="mt-2 block sm:mt-3">
                推进到下一步成交
              </span>
            </m.h2>
          </div>

          <m.div
            className="shrink-0 sm:pb-1"
            initial={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.5, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
            viewport={viewport}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <Button
              as={Link}
              className="bg-default-foreground text-background h-12 min-w-[148px] px-8 text-sm font-medium shadow-lg shadow-black/30 transition-[background-color,box-shadow,transform] duration-300 ease-out hover:bg-default-700 hover:shadow-xl hover:shadow-black/40 active:scale-[0.98]"
              href={signupHref}
              radius="full"
            >
              免费试用
            </Button>
          </m.div>
        </LazyMotion>
      </div>
    </section>
  );
}
